import { Injectable } from '@angular/core';
import { SocketMiddlewareService } from './socket-middleware.service';
import { CustomGraphSocketCommands } from 'ops/common/io-event/websocket-message';
import { Subject, takeUntil } from 'rxjs';
import { CustomGraphAccessEvents } from 'ops/common/models/access-settings';
import { CustomGraph } from 'ops/common/models/custom-graph';
import { CustomGraphService } from './custom-graph.service';

@Injectable({
    providedIn: 'root',
})
export class CustomGraphSocketService {
    destroySubscriptions$: Subject<void> = new Subject();
    updateGraphList$: Subject<CustomGraph[]> = new Subject<CustomGraph[]>();

    constructor(
        private socketService: SocketMiddlewareService,
        private customGraphService: CustomGraphService,
    ) {}

    startSubscriptions() {
        this.customGraphCreated();
        this.customGraphDeleted();
        this.customGraphUpdated();
        this.accessSettingsUpdated();
    }

    customGraphCreated() {
        return this.socketService
            .receiveMessageObserver(CustomGraphSocketCommands.GRAPH_CREATED)
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe((newGraph) => {
                const currentList = this.customGraphService.getListValue();
                const customGraphList = [...currentList, newGraph];
                this.customGraphService.setList(customGraphList);
                this.updateGraphList$.next(customGraphList);
            });
    }

    customGraphDeleted() {
        return this.socketService
            .receiveMessageObserver(CustomGraphSocketCommands.GRAPH_DELETED)
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe((graphId) => {
                const currentList = this.customGraphService.getListValue();
                const customGraphList = currentList.filter((graph) => graph.id !== graphId);
                this.customGraphService.setList(customGraphList);
                this.updateGraphList$.next(customGraphList);
            });
    }

    customGraphUpdated() {
        return this.socketService
            .receiveMessageObserver(CustomGraphSocketCommands.GRAPH_UPDATED)
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(({ graphId, newGraphData }) => {
                const currentList = this.customGraphService.getListValue();
                const customGraphList = currentList.map((graph) => {
                    if (graph.id == graphId) {
                        return newGraphData;
                    }
                    return graph;
                });
                this.customGraphService.setList(customGraphList);
                this.updateGraphList$.next(customGraphList);
            });
    }

    accessSettingsUpdated() {
        return this.socketService
            .receiveMessageObserver(CustomGraphAccessEvents.CustomGraphAccessUpdated)
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe((updatedAccess) => {
                const currentList = this.customGraphService.getListValue();
                if (updatedAccess) {
                    let { customGraphId, access } = updatedAccess;
                    const customGraphList = currentList.map((graph) => {
                        if (graph.id == customGraphId) {
                            graph.publicAccess = access;
                            return graph;
                        }
                        return graph;
                    });
                    this.customGraphService.setList(customGraphList);
                    this.updateGraphList$.next(customGraphList);
                } else {
                    this.customGraphService.setList(currentList);
                    this.updateGraphList$.next(currentList);
                }
            });
    }

    destroySubscriptions() {
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();
    }

    updateListSubject(): Subject<CustomGraph[]> {
        return this.updateGraphList$;
    }
}
